import React, {useEffect} from 'react'
import {useCheckboxDropdownPublish} from './CheckboxDropdownContext'

type TOption = {
    label: string
    value: string | number
    menu_label: string
    group_title?: string
}

type TCheckboxDropdown = {
    options: TOption[]
    formValue: any
    placeholder?: string
    isMulti?: boolean
    isDisabled?: boolean
    isLoading?: boolean
    isError?: boolean
    error?: string
    handleChange: (value: any) => void
}

const CheckboxDropdown: React.FC<TCheckboxDropdown> = ({
    options,
    formValue,
    isMulti,
    isDisabled,
    isLoading,
    isError,
    error,
    handleChange,
}) => {
    const selectedValues = new Set<string>(
        Array.isArray(formValue)
            ? formValue.map(String)
            : formValue != null
            ? [String(formValue)]
            : []
    )

    const selectedOptions = options.filter((o) => selectedValues.has(String(o.value)))

    const handleToggle = (option: TOption) => {
        if (isDisabled) return
        if (isMulti) {
            const isSelected = selectedValues.has(String(option.value))
            const newValue = isSelected
                ? selectedOptions.filter((o) => String(o.value) !== String(option.value))
                : [...selectedOptions, option]
            handleChange(newValue.length ? newValue : null)
        } else {
            const isSelected = selectedValues.has(String(option.value))
            handleChange(isSelected ? null : option)
        }
    }

    const handleToggleGroup = (groupOptions: Array<TOption>) => {
        if (isDisabled || !isMulti) return
        const groupValues = groupOptions.map((o) => String(o.value))
        const allGroupSelected = groupValues.every((v) => selectedValues.has(v))
        const newValue = allGroupSelected
            ? selectedOptions.filter((o) => !groupValues.includes(String(o.value)))
            : [
                  ...selectedOptions.filter((o) => !groupValues.includes(String(o.value))),
                  ...groupOptions,
              ]
        handleChange(newValue.length ? newValue : null)
    }

    const allSelected = options.length > 0 && options.every((o) => selectedValues.has(String(o.value)))

    const handleToggleAll = () => {
        if (isDisabled) return
        handleChange(allSelected ? null : options)
    }

    const publish = useCheckboxDropdownPublish()
    useEffect(() => {
        publish?.({allSelected, toggleAll: handleToggleAll})
    })

    if (isLoading) {
        return <div className='checkboxList_loading'>Загрузка...</div>
    }

    /* Группируем соседние пункты с одинаковым group_title под заголовок блока.
       Если у блока в итоге остался только один пункт — выводим его плоско,
       без заголовка (как обычный пункт списка). */
    const renderGroups: Array<{title: string | null; options: Array<TOption>}> = []
    options.forEach((option) => {
        const lastGroup = renderGroups[renderGroups.length - 1]
        if (option.group_title && lastGroup && lastGroup.title === option.group_title) {
            lastGroup.options.push(option)
        } else {
            renderGroups.push({title: option.group_title ?? null, options: [option]})
        }
    })

    const renderOption = (option: TOption, indented: boolean) => {
        const isChecked = selectedValues.has(String(option.value))
        return (
            <div
                key={option.value}
                className={`checkboxList_option${isChecked ? ' selected' : ''}${
                    indented ? ' checkboxList_option--indented' : ''
                }`}
                onClick={() => handleToggle(option)}
            >
                <input
                    type='checkbox'
                    checked={isChecked}
                    onChange={() => {}}
                    className='checkboxList_checkbox'
                />
                <span className='checkboxList_label'>{option.menu_label}</span>
            </div>
        )
    }

    return (
        <div className={`checkboxList${isDisabled ? ' checkboxList--disabled' : ''}${isError ? ' checkboxList--error' : ''}`}>
            {options.length === 0 ? (
                <div className='checkboxList_empty'>Нет вариантов</div>
            ) : (
                <>
                {renderGroups.map((group, groupIndex) => {
                    if (!group.title || group.options.length < 2) {
                        return (
                            <React.Fragment key={`flat-${groupIndex}`}>
                                {group.options.map((option) => renderOption(option, false))}
                            </React.Fragment>
                        )
                    }
                    const groupValues = group.options.map((o) => String(o.value))
                    const allGroupSelected = groupValues.every((v) => selectedValues.has(v))
                    const someGroupSelected = groupValues.some((v) => selectedValues.has(v))
                    return (
                        <div key={`group-${groupIndex}`} className='checkboxList_group'>
                            <div
                                className='checkboxList_groupTitle'
                                onClick={() => handleToggleGroup(group.options)}
                            >
                                <input
                                    type='checkbox'
                                    checked={allGroupSelected}
                                    ref={(el) => {
                                        if (el) el.indeterminate = !allGroupSelected && someGroupSelected
                                    }}
                                    onChange={() => {}}
                                    className='checkboxList_checkbox'
                                />
                                <span>{group.title}</span>
                            </div>
                            {group.options.map((option) => renderOption(option, true))}
                        </div>
                    )
                })}
                </>
            )}
            {isError && <div className='invalid_feedback'>{error}</div>}
        </div>
    )
}

export default CheckboxDropdown